import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { Settings } from "react-slick";
import { cn } from "@/lib/utils";
import "./slider-section.css";

type ArrowProps = {
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
};

const arrowClassName =
  "shadow-md rounded-full hover:shadow-lg relative z-[3] bg-white";

// eslint-disable-next-line react-refresh/only-export-components
function Arrow(props: ArrowProps & { children?: React.ReactNode }) {
  const { className, onClick, children } = props;
  return (
    <div onClick={onClick} className={cn(arrowClassName, className)}>
      {children}
    </div>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
function NextArrow(props: ArrowProps) {
  return (
    <Arrow {...props}>
      <FaChevronRight className="w-[9px] h-[15px] text-black" />
    </Arrow>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
function PrevArrow(props: ArrowProps) {
  return (
    <Arrow {...props}>
      <FaChevronLeft className="w-[9px] h-[15px] text-black" />
    </Arrow>
  );
}

export const DESKTOP_SLIDES_TO_SHOW = 8;
export const SLIDES_TO_SHOW_OVERLAP = 2;
export const SLIDE_BASE_WIDTH = 220; // card width (200px) + 20px gap

export function getSlidesToScroll(slidesToShow: number) {
  return Math.max(1, slidesToShow - SLIDES_TO_SHOW_OVERLAP);
}

const DEFAULT_SLICK_SETTINGS: Settings = {
  dots: false,
  infinite: false,
  centerMode: false,
  lazyLoad: "progressive",
  slidesToShow: DESKTOP_SLIDES_TO_SHOW,
  slidesToScroll: getSlidesToScroll(DESKTOP_SLIDES_TO_SHOW),
  initialSlide: 0,
  nextArrow: <NextArrow />,
  prevArrow: <PrevArrow />,
};

export function useSliderSettings(
  settingsOverrides: Partial<Settings> = {},
): Settings {
  const merged: Settings = { ...DEFAULT_SLICK_SETTINGS, ...settingsOverrides };

  if (typeof merged.slidesToShow === "number") {
    const defaultScroll = getSlidesToScroll(merged.slidesToShow);
    const shouldForceSingleScroll = merged.slidesToShow < 3;
    const derivedScroll = shouldForceSingleScroll ? 1 : defaultScroll;

    if (typeof merged.slidesToScroll !== "number") {
      merged.slidesToScroll = derivedScroll;
    } else if (shouldForceSingleScroll && merged.slidesToScroll > 1) {
      merged.slidesToScroll = 1;
    }
  }

  return merged;
}

export function getSlidesToShowForWidth(
  width?: number,
  slideBaseWidth: number = SLIDE_BASE_WIDTH,
) {
  if (!width || width <= 0) {
    return DESKTOP_SLIDES_TO_SHOW;
  }

  // Use fractional counts so mobile still shows a second card peeking in.
  const slides = width / slideBaseWidth;
  return Math.max(1.25, Math.min(DESKTOP_SLIDES_TO_SHOW, slides));
}
